import { Component } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  Client,
  DownloadBulkDataIn,
  DownloadLoadProfileDataIn,
  PageInfo,
  PermissionsEnum, QueryScheduledDataReadDataIn, QueryScheduledDataReadDataOut,
  SortingType,
} from '../../../api/api-reference';
import { DateRangeInput } from '../../core/models/date-range-input';
import { SortComponent } from '../../core/components/sort/sort.component';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { CommonModule, DatePipe } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { SecondsToText } from '../../../pipes/seconds-to-text';
import { ClipboardService } from '../../../services/clipboard.service';
import { FileService } from "../../../services/file.service";
import { Router } from '@angular/router';
import { generateFileNameAppendix } from "../../../helper/download-filename";
import { AngularSvgIconModule } from 'angular-svg-icon';
import { formatDateRange, processDateRange } from '../../../helper/date-utils';


@Component({
  selector: 'app-energy-per-phase-report',
  standalone: true,
  imports: [
    TranslateModule,
    SortComponent,
    DatepickerComponent,
    SearchComponent,
    PaginationComponent,
    DatePipe,
    SecondsToText,
    CommonModule,
    AngularSvgIconModule
  ],
  templateUrl: './energy-per-phase.component.html',
  styleUrl: './energy-per-phase.component.scss'
})
export class EnergyPerPhaseComponent {
  data: QueryScheduledDataReadDataOut[] = [];
  public PermissionsEnum = PermissionsEnum;
  selectedDate: string | null = '';

  private selection: Map<string, QueryScheduledDataReadDataOut> = new Map();
  get isHeaderChecked(): boolean {
    return this.data.length > 0 && this.data.every(obj => this.selection.has(obj.meterId));
  }
  get selectedRows(): string[] { return Array.from(this.selection.keys()); }

  isDatepickerOpen = false;
  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public lastPageSize: number = 1;
  public pageSize: number = 10;
  public pageNumber: number = 1;
  readonly dayInMs = 24 * 60 * 60 * 1000;

  requestData = new QueryScheduledDataReadDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    startDate: undefined,
    endDate: undefined,
    search: undefined,
    sorting: SortingType.Descending,
    assetTypes: [],
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private router: Router,
    public authService: AuthService,
    private fileService: FileService,
    public clipboardService: ClipboardService
  ) {
    this.reloadTable(1);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.queryLastScheduledDevices(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data!;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.clearSelection();
    this.reloadTable(1);
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  ensureDate(dateTime: unknown): Date | null {
    return dateTime instanceof Date ? dateTime : null;
  }

  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.clearSelection();
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.clearSelection();
    this.reloadTable();
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  filterDatesInTable() {
    this.client.queryLastScheduledDevices(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data!;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  rowClick(meterId: string) {
    this.router.navigate(['/analysis-report/energy-per-phase-details'], {
      queryParams: { meterId: meterId },
    });
  }

  isRowSelected(meterId: string) {
    return this.selection.has(meterId);
  }
  toggleAllSelected(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      let limited = false;
      for (const obj of this.data) {
        if (this.selection.size >= 100) { limited = true; break; }
        if (!this.selection.has(obj.meterId)) this.selection.set(obj.meterId, obj);
      }
      if (limited) {
        this.toastr.warning('You can select a maximum of 100 entities for bulk actions.');
      }
    } else {
      this.data.forEach((obj) => this.selection.delete(obj.meterId));
    }
  }
  toggleRowSelection(row: QueryScheduledDataReadDataOut, event: Event) {
    event.stopPropagation();
    if (this.selection.has(row.meterId)) {
      this.selection.delete(row.meterId);
    } else {
      if (this.selection.size >= 100) {
        event.preventDefault();
        this.toastr.warning('You can select a maximum of 100 entities for bulk actions.');
        return;
      }
      this.selection.set(row.meterId, row);
    }
  }
  clearSelection() {
    this.selection.clear();
  }

  downloadSelected() {
    const now = new Date();
    const startDate = this.requestData.startDate ?? new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = this.requestData.endDate ?? new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

    const data = new DownloadBulkDataIn({
      meters: this.selectedRows,
      startDate,
      endDate,
    });

    this.client.downloadBulkEnergyPerPhaseCombined(data).subscribe({
      next: (response) => {
        if (response?.includes(' ')) {
          this.toastr.warning(response);
        } else {
          this.client.downloadBulkFile(response).subscribe({
            next: (file) => {
              const extension = file.data.type === 'application/zip' ? 'zip' : 'xlsx';
              const fileNameAppendix = generateFileNameAppendix(extension);
              this.fileService.downloadFile(file.data, file.fileName || `BulkEnergyPerPhase${fileNameAppendix}`);
            },
            error: (err) => this.toastr.error(err),
          });
        }
      },
      error: (err) => this.toastr.error(err),
    });
  }

  download(meterId: string) {
    const fileNameApendix = generateFileNameAppendix();

    const data = new DownloadLoadProfileDataIn({
      meterId: meterId,
      startDate: this.requestData.startDate,
      endDate: this.requestData.endDate
    })
    this.client.downloadEnergyPerPhaseFile(data).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `EnergyPerPhase${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
